// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getMergedAttributeTypeFacets,
    getProductsDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    CategoryFacetModel,
    FacetModel,
    PriceFacetModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import { formatPriceRangeFacet } from "@insite/content-library/Widgets/ProductList/ProductListPriceFilters";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const enum fields {
    viewType = "viewType",
}

interface OwnProps extends WidgetProps {
    // XNG-Changes: add new fields
    fields: {
        [fields.viewType]: "title" | "tags";
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency, language },
        },
    } = state;
    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;

        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;

        return {
            loaded: true,
            stockedItemsOnly: productFilters.stockedItemsOnly ?? false,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts ?? false,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: getMergedAttributeTypeFacets(state, unfilteredApiParameter, filteredApiParameter),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributeValueIds,
            selectedCategoryId: productFilters.categoryId,
            currency,
            language,
            isEstimatedItemCount: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        };
    }
    return {
        loaded: false,
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        brandFacets: [] as FacetModel[],
        productLineFacets: [] as FacetModel[],
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        attributeTypeFacets: [] as AttributeTypeFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        language: null,
        isEstimatedItemCount: false,
    };
};

const mapDispatchToProps = {
    removeProductFilters,
    clearAllProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListFilterListStyles {
    wrapper?: InjectableCss;
    skipFiltersButtonWrapper?: InjectableCss;
    skipFiltersButton?: SkipNavStyles;
    filterText?: TypographyPresentationProps;
    filterTag?: TagPresentationProps;
    clearAllLink?: LinkPresentationProps;
    filterTextWrapper?: InjectableCss;
    tooltip?: TooltipPresentationProps;
    // XNG-Changes:
    filterTagsWrapper?: InjectableCss;
}

export const filterListStyles: ProductListFilterListStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    filterText: {
        variant: "h3",
    },
    filterTextWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            gap: 8px;
        `,
    },
    tooltip: {
        cssOverrides: {
            tooltipClickable: css`
                display: inline-flex;
                margin-bottom: 2px;
            `,
            tooltipBody: css`
                font-size: 14px;
                font-weight: normal;
                text-transform: none;
                line-height: 1.5;
            `,
        },
    },
    skipFiltersButtonWrapper: {
        css: css`
            position: relative;
            top: -65px;
            width: 100%;
        `,
    },
    clearAllLink: {
        css: css`
            padding-bottom: 20px;
        `,
    },
};

const styles = filterListStyles;

const ProductListFilterList: FC<Props> = ({
    loaded,
    stockedItemsOnly,
    previouslyPurchasedProducts,
    brandFacets,
    productLineFacets,
    priceFacets,
    categoryFacets,
    attributeTypeFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    removeProductFilters,
    clearAllProductFilters,
    currency,
    language,
    isEstimatedItemCount,
    fields: { viewType },
}) => {
    if (!loaded) {
        return null;
    }

    // XNG-Changes: showing unique filter results to solve race condition which can duplicate tags
    const uniqueSelectedAttributeValuesIds = React.useMemo(() => {
        return Array.from(new Set(selectedAttributeValuesIds));
    }, [selectedAttributeValuesIds]);

    const uniqueSelectedBrandIds = React.useMemo(() => {
        return Array.from(new Set(selectedBrandIds));
    }, [selectedBrandIds]);

    const uniqueSelectedProductLineIds = React.useMemo(() => {
        return Array.from(new Set(selectedProductLineIds));
    }, [selectedProductLineIds]);

    const uniqueSelectedPriceFilters = React.useMemo(() => {
        return Array.from(new Set(selectedPriceFilters));
    }, [selectedPriceFilters]);
    // End of XNG-Changes
    const clickRemoveStockedItemsOnly = () => {
        removeProductFilters({ stockedItemsOnly: false });
    };

    const clickRemovePreviouslyPurchasedProducts = () => {
        removeProductFilters({ previouslyPurchasedProducts: false });
    };

    const clickRemoveSearchHandler = (search: string) => {
        removeProductFilters({ searchWithinQueries: [search] });
    };

    const clickRemoveBrandHandler = (id: string) => {
        removeProductFilters({ brandIds: [id] });
    };

    const clickRemoveProductLineHandler = (id: string) => {
        removeProductFilters({ productLineIds: [id] });
    };

    const clickRemovePriceHandler = (price: string) => {
        removeProductFilters({ priceFilters: [price] });
    };

    const clickRemoveAttributeValueHandler = (id: string) => {
        removeProductFilters({ attributeValueIds: [id] });
    };

    const clickRemoveCategoryHandler = () => {
        removeProductFilters({ categoryId: selectedCategoryId });
    };

    // XNG-Changes
    // add categoryFacets?.length to the condition
    const anyActiveFilters =
        searchWithinQueries?.length ||
        stockedItemsOnly ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        (selectedCategoryId && categoryFacets?.length);

    // XNG-Changes
    if (viewType === "title") {
        return (
            <>
                <Typography {...styles.filterText} data-test-selector="productListFilterList">
                    {translate("Facets Filter")}
                </Typography>
                <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                    <ProductListPageDataContext.Consumer>
                        {({ ref }) => {
                            return ref ? (
                                <SkipNav
                                    text={translate("Skip to Results")}
                                    extendedStyles={styles.skipFiltersButton}
                                    destination={ref}
                                />
                            ) : undefined;
                        }}
                    </ProductListPageDataContext.Consumer>
                </StyledWrapper>
            </>
        );
    }

    const estimatedItemCountText = "Product counts are estimated and may update as you apply filter.";

    return (
        <StyledWrapper {...styles.wrapper}>
            {isEstimatedItemCount ? (
                <StyledWrapper {...styles.filterTextWrapper}>
                    <Typography {...styles.filterText} data-test-selector="productListFilterList">
                        {translate("Filter")}
                    </Typography>
                    <Tooltip {...styles.tooltip} text={translate(estimatedItemCountText)} />
                </StyledWrapper>
            ) : (
                <Typography {...styles.filterText} data-test-selector="productListFilterList">
                    {translate("Filter")}
                </Typography>
            )}
            <StyledWrapper {...styles.filterTagsWrapper}>
                {/* XNG-Changes: comment-out OOTB code
                <Typography {...styles.filterText} data-test-selector="productListFilterList">
                    {translate("Facets Filter")}
                </Typography>
                <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                    <ProductListPageDataContext.Consumer>
                        {({ ref }) => {
                            return ref ? (
                                <SkipNav
                                    text={translate("Skip to Results")}
                                    extendedStyles={styles.skipFiltersButton}
                                    destination={ref}
                                />
                            ) : undefined;
                        }}
                    </ProductListPageDataContext.Consumer>
                </StyledWrapper> 
                */}
                {previouslyPurchasedProducts && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={clickRemovePreviouslyPurchasedProducts}
                        data-test-selector="previouslyPurchasedProductsFilter"
                    >
                        {translate("Previously Purchased Only")}
                    </Tag>
                )}
                {stockedItemsOnly && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={clickRemoveStockedItemsOnly}
                        data-test-selector="stockedItemsOnlyFilter"
                    >
                        {translate("Stocked Items Only")}
                    </Tag>
                )}
                {searchWithinQueries?.map(s => (
                    <Tag
                        key={s}
                        {...styles.filterTag}
                        onDelete={() => clickRemoveSearchHandler(s)}
                        data-test-selector="searchWithinFilter"
                    >
                        {translate("Search")}: {s}
                    </Tag>
                ))}
                {/* XNG-Change */}
                {brandFacets &&
                    uniqueSelectedBrandIds?.map(id => (
                        <Tag
                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveBrandHandler(id)}
                            data-test-selector={`brandFilter${id}`}
                        >
                            {translate("Brand")}: {brandFacets?.find(o => o.id === id)?.name}
                        </Tag>
                    ))}
                {/* XNG-Change */}
                {productLineFacets &&
                    uniqueSelectedProductLineIds?.map(id => (
                        <Tag
                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveProductLineHandler(id)}
                            data-test-selector={`productLineFilter${id}`}
                        >
                            {translate("Product Line")}: {productLineFacets?.find(o => o.id === id)?.name}
                        </Tag>
                    ))}
                {/* XNG-Changes */}
                {/* add categoryFacets?.length to the showing condition */}
                {selectedCategoryId && categoryFacets?.length && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={() => clickRemoveCategoryHandler()}
                        data-test-selector={`categoryFilter${selectedCategoryId}`}
                    >
                        {translate("Category")}:{" "}
                        {categoryFacets?.find(o => o.categoryId === selectedCategoryId)?.shortDescription}
                    </Tag>
                )}
                {/* XNG-Change */}
                {priceFacets &&
                    uniqueSelectedPriceFilters?.map(id => (
                        <Tag
                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemovePriceHandler(id)}
                            data-test-selector={`priceFilter${id}`}
                        >
                            {translate("Price")}:{" "}
                            {formatPriceRangeFacet(
                                priceFacets?.find(o => o.minimumPrice.toString() === id),
                                currency,
                                language,
                            )}
                        </Tag>
                    ))}
                {/* XNG-Change */}
                {attributeTypeFacets &&
                    uniqueSelectedAttributeValuesIds?.map(id => {
                        const attributeTypeFacet = attributeTypeFacets?.find(at =>
                            at.attributeValueFacets?.find(av => av.attributeValueId === id),
                        );
                        const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                            av => av.attributeValueId === id,
                        );

                        return (
                            <Tag
                                key={id}
                                {...styles.filterTag}
                                onDelete={() => clickRemoveAttributeValueHandler(id)}
                                data-test-selector={`attributeValueFilter${id}`}
                            >
                                {attributeTypeFacet?.nameDisplay}: {attributeValueFacet?.valueDisplay}
                            </Tag>
                        );
                    })}
            </StyledWrapper>
            {/* XNG-Changes: changed label */}
            {anyActiveFilters && (
                <Link
                    {...styles.clearAllLink}
                    onClick={() => clearAllProductFilters()}
                    data-test-selector="productListClearAll"
                >
                    {translate("Clear Search Terms")}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListFilterList),
    definition: {
        group: "Product List",
        displayName: "Filter List",
        allowedContexts: ["ProductListPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.viewType,
                displayName: "Display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Title",
                        value: "title",
                    },
                    {
                        displayName: "Tags",
                        value: "tags",
                    },
                ],
                defaultValue: "title",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
